/**
 * roleAccess.js
 * Single source of truth for IM-Intake-Form role gating.
 *
 * Roles come from the Secrets Manager users secret (user:sha256pass:role) and are
 * stored at login in localStorage `auth_userrole`. These gates are honest-actor
 * enforcement on the client; the only hard server check is the contracts/admin
 * 403 on inaccuracy resolution. Update docs/ROLES.md whenever this file changes.
 */

const ROLE_ALIASES = {
  'service engineer': 'engineer',
  'service_engineer': 'engineer',
  'service-engineer': 'engineer',
};

const CANONICAL_ROLES = ['admin', 'qa', 'im', 'ae', 'engineer', 'contracts'];

// Statuses of the AE-claim review loop (mirrors REVIEW_LOOP_STATUSES in the lambda).
const REVIEW_LOOP_STATUSES = ['inaccurate_data_claim', 'new_quote_needed', 'needs_im_revision'];

export function normalizeRole(raw) {
  const lowered = String(raw || '').trim().toLowerCase();
  const mapped = ROLE_ALIASES[lowered] || lowered;
  return CANONICAL_ROLES.includes(mapped) ? mapped : 'restricted';
}

export function currentRole() {
  return normalizeRole(localStorage.getItem('auth_userrole'));
}

export const ALLOWED_TABS = {
  admin: ['intake', 'queue', 'checklist', 'audit', 'install-complete'],
  qa: ['queue', 'audit'],
  im: ['intake', 'queue'],
  ae: ['queue'],
  engineer: ['queue', 'checklist', 'audit', 'install-complete'],
  contracts: ['queue'],
  restricted: ['queue'],
};

// Inner chrome nav entries shared by all five tab components.
export const CHROME_TABS = [
  { id: 'intake', label: 'Implementation Intake' },
  { id: 'queue', label: 'Queue' },
  { id: 'checklist', label: 'Service Install Checklist' },
  { id: 'audit', label: 'Audit' },
];

export function isTabAllowed(role, tab) {
  return (ALLOWED_TABS[normalizeRole(role)] || ALLOWED_TABS.restricted).includes(tab);
}

export function defaultTabForRole(role) {
  const r = normalizeRole(role);
  return r === 'admin' || r === 'im' ? 'intake' : 'queue';
}

// ── Capabilities ──
export const canCreateIntake = (role) => ['im', 'admin'].includes(normalizeRole(role));
export const canResolveClaim = (role) => ['contracts', 'admin'].includes(normalizeRole(role));
export const canViewClaimReadOnly = (role) => normalizeRole(role) === 'im';
export const canDownloadChecklists = (role) => !['ae', 'restricted'].includes(normalizeRole(role));
export const canOpenIntakeCase = (role) => ['im', 'admin'].includes(normalizeRole(role));
export const canValidateInstall = (role) => ['im', 'engineer', 'admin'].includes(normalizeRole(role));
export const canOpenChecklistTab = (role) => ['engineer', 'admin'].includes(normalizeRole(role));

/**
 * Queue row visibility. AE filtering is by status only (no name matching):
 * their own pending sign-offs plus anything in the claim review loop.
 * Depends on q.rawStatus (true intake status) because the queue status
 * collapses pending_signoff into in_progress.
 */
export function queueStatusFilterForRole(role, q) {
  const r = normalizeRole(role);
  if (r === 'ae') return q.rawStatus === 'pending_signoff' || REVIEW_LOOP_STATUSES.includes(q.status);
  if (r === 'contracts') return REVIEW_LOOP_STATUSES.includes(q.status);
  return true;
}

/** AE-friendly status label overrides; returns null to fall back to the default label. */
export function roleStatusLabel(role, q) {
  if (normalizeRole(role) !== 'ae') return null;
  if (q.status === 'inaccurate_data_claim') return 'Pending contracts review';
  if (q.status === 'needs_im_revision') return 'Claim approved — IM revising';
  if (q.rawStatus === 'pending_signoff') return 'Awaiting your sign-off';
  return null;
}
